import React from 'react';
import { motion } from 'framer-motion';
import { PackageX, TrendingDown, Clock, ShieldAlert, Check, ArrowRight } from 'lucide-react';
const services = [{
  icon: PackageX,
  title: 'Surplus & Overstock Purchasing',
  description: 'We purchase and facilitate the sale of excess inventory, overstocked goods, end-of-line products, discontinued ranges, cancelled order stock, and seasonal clearance.',
  benefits: ['Excess Inventory', 'End-of-Line Products', 'Cancelled Orders', 'Seasonal Clearance']
}, {
  icon: TrendingDown,
  title: 'Large Volume Stock Clearance',
  description: 'For companies holding large stock volumes, we identify bulk buyers locally and internationally, coordinate secondary distribution channels, and negotiate structured clearance agreements.',
  benefits: ['Bulk Buyer Network', 'Secondary Channels', 'Clearance Agreements', 'Fast Turnaround']
}, {
  icon: Clock,
  title: 'Short-Dated & Near-Expiry Management',
  description: 'We assist with responsible, compliant distribution of products with close or near expiry dates — connecting with discount retailers, export buyers, and rapid turnover channels.',
  benefits: ['Secondary Markets', 'Discount Retailers', 'Rapid Turnover', 'Regulatory Compliance']
}, {
  icon: ShieldAlert,
  title: 'Confidential & Brand-Sensitive Liquidation',
  description: 'We structure discreet sales channels that protect pricing integrity, avoid conflict with primary retail partners, and control geographic redistribution.',
  benefits: ['Discreet Channels', 'Price Protection', 'Partner Safeguards', 'Controlled Distribution']
}];
const reasons = ['Fast inventory turnaround', 'Access to international clearance buyers', 'Large volume handling capability', 'Structured liquidation strategy', 'Compliance-focused distribution', 'Brand-sensitive execution', 'Recovery of tied-up capital'];
export function StockLiquidationSection() {
  return <section id="stock-liquidation" className="py-24 bg-[#0a1628] relative overflow-hidden" data-id="element-517">
      {/* Accent stripe */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#d4a853]/40 to-transparent" data-id="element-518" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-519">
        {/* Header */}
        <div className="flex flex-col lg:flex-row gap-12 mb-16" data-id="element-520">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="lg:w-1/2" data-id="element-521">
            <span className="text-[#d4a853] font-medium tracking-[0.2em] uppercase text-sm block mb-4" data-id="element-522">
              Stock Liquidation Solutions
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-523">
              Turning Excess Inventory into{' '}
              <span className="text-[#d4a853]" data-id="element-524">Opportunity</span>
            </h2>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="lg:w-1/2" data-id="element-525">
            <p className="text-gray-400 text-lg leading-relaxed mb-6" data-id="element-526">
              We provide professional stock liquidation and surplus inventory
              solutions for manufacturers, distributors, and retail groups
              across the UK, Europe, and Asia.
            </p>
            <p className="text-gray-400 leading-relaxed" data-id="element-527">
              Excess inventory, discontinued lines, overproduction, or
              short-dated stock can tie up capital and warehouse space. Our role
              is to help businesses recover value efficiently, discreetly, and
              compliantly.
            </p>
          </motion.div>
        </div>

        {/* 4 Service Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16" data-id="element-528">
          {services.map((service, index) => <motion.div key={service.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="bg-[#1a2d4a]/30 border border-white/10 p-8 hover:border-[#d4a853] transition-colors duration-300 group" data-id="element-529">
              <div className="p-3 bg-[#1a2d4a] group-hover:bg-[#d4a853] transition-colors duration-300 rounded-sm w-fit mb-6" data-id="element-530">
                <service.icon className="w-6 h-6 text-[#d4a853] group-hover:text-[#0a1628] transition-colors duration-300" data-id="element-531" />
              </div>

              <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#d4a853] transition-colors" data-id="element-532">
                {service.title}
              </h3>

              <p className="text-gray-400 text-sm leading-relaxed mb-6" data-id="element-533">
                {service.description}
              </p>

              <div className="grid grid-cols-2 gap-2" data-id="element-534">
                {service.benefits.map((benefit, i) => <div key={i} className="flex items-center gap-2 text-xs text-gray-400" data-id="element-535">
                    <Check className="w-3 h-3 text-[#d4a853] flex-shrink-0" data-id="element-536" />
                    <span data-id="element-537">{benefit}</span>
                  </div>)}
              </div>
            </motion.div>)}
        </div>

        {/* Bottom CTA Bar */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-[#1a2d4a] border border-white/10 p-8 lg:p-12" data-id="element-538">
          <div className="flex flex-col lg:flex-row gap-12 items-start" data-id="element-539">
            {/* Left: Why Work With Us */}
            <div className="lg:w-1/2" data-id="element-540">
              <h3 className="text-2xl font-bold text-white mb-6" data-id="element-541">
                Why Work With Us for Liquidation?
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-542">
                {reasons.map((reason, i) => <motion.div key={i} initial={{
                opacity: 0,
                x: -10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.05
              }} className="flex items-center gap-3 text-sm text-gray-300" data-id="element-543">
                    <div className="w-1.5 h-1.5 bg-[#d4a853] rounded-full flex-shrink-0" data-id="element-544" />
                    {reason}
                  </motion.div>)}
              </div>
            </div>

            {/* Right: Target Audiences + CTA */}
            <div className="lg:w-1/2" data-id="element-545">
              <h3 className="text-lg font-bold text-white mb-4" data-id="element-546">
                Whether you are a:
              </h3>
              <div className="space-y-3 mb-8" data-id="element-547">
                {['Manufacturer with surplus production', 'Distributor holding excess stock', 'Hypermarket clearing seasonal lines', 'Brand managing short-dated inventory'].map((audience, i) => <div key={i} className="flex items-center gap-3 text-gray-400" data-id="element-548">
                    <Check className="w-4 h-4 text-[#d4a853] flex-shrink-0" data-id="element-549" />
                    <span data-id="element-550">{audience}</span>
                  </div>)}
              </div>

              <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={() => {
              const el = document.getElementById('contact');
              if (el) window.scrollTo({
                top: el.offsetTop - 80,
                behavior: 'smooth'
              });
            }} className="group relative px-8 py-4 bg-[#d4a853] text-[#0a1628] font-bold uppercase tracking-wider overflow-hidden" data-id="element-551">
                <span className="relative z-10 flex items-center gap-2" data-id="element-552">
                  Discuss Stock Liquidation
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-553" />
                </span>
                <div className="absolute inset-0 bg-white transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300 ease-out" data-id="element-554" />
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}